import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, RefreshCw, Plus, Minus, Repeat, Trash2, ArrowUp, ArrowDown, Link2, Dumbbell, AlertTriangle } from 'lucide-react';
import ExerciseSelectorGrid from './workout/ExerciseSelectorGrid';
import { isCardio, secondsToMMSS, mmssToSeconds } from '../utils/cardio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SS_GROUPS = ['A', 'B', 'C', 'D', 'E'];

// Module-level cache: the full exercise library is stable within a Live Board
// session, so we fetch it once and reuse across coach-editor open/close.
let _exerciseListCache = null;

// Trainer-facing structural editor driven from the Live Board (trainer's own
// device). All writes go through the /coach/* endpoints with the X-Coach-Mode
// header + trainer auth — the same permission path used on the client tablet,
// so structure changes never get clobbered by the client's autosave.
const coachHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'X-Coach-Mode': 'true' };
};

const LiveBoardCoachEditor = ({ workoutId, clientName, onClose }) => {
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [picker, setPicker] = useState(null); // { mode: 'add' | 'replace', index }
  const [weightDraft, setWeightDraft] = useState({}); // index -> string
  const [tab, setTab] = useState('structure'); // 'structure' | 'logged'
  const [setDrafts, setSetDrafts] = useState({}); // index -> [{reps, weight, rpe}]
  const [allExercises, setAllExercises] = useState([]); // full library for the picker
  const [exerciseUsage, setExerciseUsage] = useState({}); // this client's history

  const fetchWorkout = useCallback(async () => {
    try {
      setError(null);
      const res = await axios.get(`${API}/workouts/${workoutId}`, { headers: coachHeaders() });
      setWorkout(res.data);
      const drafts = {};
      const sdrafts = {};
      (res.data.exercises || []).forEach((ex, i) => {
        drafts[i] = String(ex.target_weight ?? ex.weight ?? 0);
        sdrafts[i] = (ex.actual_sets || []).map((s) => ({
          reps: s.reps ?? '', weight: s.weight ?? '', rpe: s.rpe ?? '',
          time: s.time != null ? secondsToMMSS(s.time) : '', rest_time: s.rest_time ?? '',
        }));
      });
      setWeightDraft(drafts);
      setSetDrafts(sdrafts);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load workout');
    } finally { setLoading(false); }
  }, [workoutId]);

  useEffect(() => { fetchWorkout(); }, [fetchWorkout]);

  // Load the full exercise library once (cached across editor opens).
  useEffect(() => {
    if (_exerciseListCache) { setAllExercises(_exerciseListCache); return; }
    let alive = true;
    axios.get(`${API}/exercises/list`, { headers: coachHeaders() })
      .then((res) => { _exerciseListCache = res.data || []; if (alive) setAllExercises(_exerciseListCache); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Load THIS client's exercise usage history so the picker shows last-used /
  // ×count badges, same as the on-device coach picker.
  useEffect(() => {
    const cid = workout?.client_id;
    if (!cid) return;
    let alive = true;
    axios.get(`${API}/exercises/client-usage?client_id=${cid}`, { headers: coachHeaders() })
      .then((res) => { if (alive) setExerciseUsage(res.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [workout?.client_id]);

  const run = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); await fetchWorkout(); }
    catch (e) { setError(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const exercises = workout?.exercises || [];
  const getSets = (ex) => Number(ex.target_sets ?? ex.sets ?? 0);
  const getReps = (ex) => Number(ex.target_reps ?? ex.reps ?? 0);
  const getWeight = (ex) => Number(ex.target_weight ?? ex.weight ?? 0);

  const updateTargets = (index, updates) =>
    run(() => axios.put(`${API}/workouts/${workoutId}/coach/update-targets/${index}`, updates, { headers: coachHeaders() }));

  const changeSets = (index, delta) => {
    const next = Math.max(1, getSets(exercises[index]) + delta);
    updateTargets(index, { target_sets: next });
  };

  const commitWeight = (index) => {
    const val = parseFloat(weightDraft[index]);
    if (isNaN(val) || val === getWeight(exercises[index])) return;
    updateTargets(index, { target_weight: Math.max(0, val) });
  };

  const removeExercise = (index) => {
    if (!window.confirm('Remove this exercise from the client\'s session?')) return;
    run(() => axios.delete(`${API}/workouts/${workoutId}/coach/remove-exercise/${index}`, { headers: coachHeaders() }));
  };

  const moveExercise = (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= exercises.length) return;
    const order = exercises.map((_, i) => i);
    const [m] = order.splice(index, 1);
    order.splice(to, 0, m);
    run(() => axios.put(`${API}/workouts/${workoutId}/coach/reorder-exercises`, { exercise_order: order }, { headers: coachHeaders() }));
  };

  const toggleSuperset = (index) => {
    const ex = exercises[index];
    const next = !ex.is_superset;
    run(() => axios.put(`${API}/workouts/${workoutId}/coach/update-superset/${index}`,
      { is_superset: next, superset_group: next ? (ex.superset_group || 'A') : null }, { headers: coachHeaders() }));
  };

  const setSupersetGroup = (index, group) => {
    run(() => axios.put(`${API}/workouts/${workoutId}/coach/update-superset/${index}`,
      { is_superset: true, superset_group: group }, { headers: coachHeaders() }));
  };

  // --- Logged-values (actual_sets) editing ---
  const updateSetDraft = (exIdx, setIdx, field, value) => {
    setSetDrafts((d) => {
      const rows = [...(d[exIdx] || [])];
      rows[setIdx] = { ...rows[setIdx], [field]: value };
      return { ...d, [exIdx]: rows };
    });
  };
  const addSetDraft = (exIdx) => {
    setSetDrafts((d) => ({ ...d, [exIdx]: [...(d[exIdx] || []), { reps: '', weight: '', rpe: '', time: '', rest_time: '' }] }));
  };
  const removeSetDraft = (exIdx, setIdx) => {
    setSetDrafts((d) => ({ ...d, [exIdx]: (d[exIdx] || []).filter((_, i) => i !== setIdx) }));
  };
  const saveActualSets = (exIdx) => {
    const cardio = isCardio(exercises[exIdx]);
    const rows = (setDrafts[exIdx] || []).map((s) => cardio ? ({
      time: mmssToSeconds(s.time),
      rest_time: s.rest_time === '' ? null : Number(s.rest_time),
      reps: 1,
      weight: 0,
      completed: true,
    }) : ({
      reps: s.reps === '' ? null : Number(s.reps),
      weight: s.weight === '' ? null : Number(s.weight),
      rpe: s.rpe === '' ? null : Number(s.rpe),
      completed: true,
    }));
    run(() => axios.put(`${API}/workouts/${workoutId}/coach/update-actual-sets/${exIdx}`,
      { actual_sets: rows }, { headers: coachHeaders() }));
  };

  const handlePick = (exercise) => {
    if (!picker) return;
    if (picker.mode === 'replace') {
      // Keep the load: prefill the new exercise with this client's last logged
      // weight for it (falls back to preserved sets/reps + 0 kg when unknown).
      const lastWeight = exerciseUsage[exercise.id]?.last_weight;
      run(() => axios.post(`${API}/workouts/${workoutId}/coach/replace-exercise`,
        { exercise_index: picker.index, new_exercise_id: exercise.id, preserve_sets: true,
          target_weight: (lastWeight != null && lastWeight > 0) ? lastWeight : null }, { headers: coachHeaders() }));
      setPicker(null); // one substitution → close
    } else {
      // Add mode: keep the picker open so the trainer can add several in a row.
      run(() => axios.post(`${API}/workouts/${workoutId}/coach/add-exercise`,
        { exercise_id: exercise.id, position: null, target_sets: 4, target_reps: 12, target_weight: 0, rest_time: 120, is_superset: false, superset_group: null }, { headers: coachHeaders() }));
    }
  };

  // For "add" mode, hide exercises already in the session; "replace" shows all.
  const pickerExercises = picker?.mode === 'add'
    ? allExercises.filter((e) => !exercises.some((w) => w.exercise_id === e.id))
    : allExercises;

  return (
    <>
      <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/70 p-4" data-testid="coach-editor-modal">
        <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[92dvh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-2 min-w-0">
              <Dumbbell className="w-5 h-5 text-orange-400 shrink-0" />
              <div className="min-w-0">
                <h3 className="text-white font-bold truncate">Coach edit — {clientName}</h3>
                <p className="text-blue-300/70 text-xs">Changes apply live; the client can refresh on their screen.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchWorkout} disabled={busy}
                className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50"
                data-testid="coach-editor-refresh" title="Refresh">
                <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white" data-testid="coach-editor-close">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {error && (
            <div className="mx-4 mt-3 flex items-center gap-2 text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-2.5" data-testid="coach-editor-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 px-4 pt-3">
            <button onClick={() => setTab('structure')}
              className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${tab === 'structure' ? 'bg-orange-500 text-black' : 'bg-slate-700 text-blue-200 hover:bg-slate-600'}`}
              data-testid="coach-editor-tab-structure">Structure</button>
            <button onClick={() => setTab('logged')}
              className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${tab === 'logged' ? 'bg-orange-500 text-black' : 'bg-slate-700 text-blue-200 hover:bg-slate-600'}`}
              data-testid="coach-editor-tab-logged">Logged values</button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
            {loading ? (
              <p className="text-blue-300/70 text-sm py-6 text-center">Loading…</p>
            ) : exercises.length === 0 ? (
              <p className="text-blue-300/70 text-sm py-6 text-center">No exercises.</p>
            ) : tab === 'structure' ? exercises.map((ex, i) => {
              const isCardio = (ex.exercise_type || '').toLowerCase() === 'cardio';
              return (
                <div key={`${ex.exercise_id}-${i}`} className="bg-slate-800/60 rounded-xl p-3 border border-slate-700/60" data-testid={`coach-editor-ex-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-white font-semibold text-sm">{ex.exercise_name_es || ex.exercise_name}</span>
                        {isCardio && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">Cardio</span>}
                        {ex.is_superset && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300 border border-orange-500/40">SS {ex.superset_group || 'A'}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => moveExercise(i, -1)} disabled={busy || i === 0} className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-30" data-testid={`coach-editor-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveExercise(i, 1)} disabled={busy || i === exercises.length - 1} className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-30" data-testid={`coach-editor-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setPicker({ mode: 'replace', index: i })} disabled={busy} className="p-1.5 rounded bg-blue-600/70 hover:bg-blue-500 text-white disabled:opacity-40" data-testid={`coach-editor-replace-${i}`} title="Substitute"><Repeat className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeExercise(i)} disabled={busy || exercises.length <= 1} className="p-1.5 rounded bg-red-600/60 hover:bg-red-500 text-white disabled:opacity-30" data-testid={`coach-editor-remove-${i}`} title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>

                  {!isCardio && (
                    <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                      {/* Sets */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-blue-300/70 w-9">Sets</span>
                        <button onClick={() => changeSets(i, -1)} disabled={busy || getSets(ex) <= 1} className="p-1 rounded bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-30" data-testid={`coach-editor-set-minus-${i}`}><Minus className="w-3.5 h-3.5" /></button>
                        <span className="text-white font-mono w-6 text-center" data-testid={`coach-editor-sets-${i}`}>{getSets(ex)}</span>
                        <button onClick={() => changeSets(i, 1)} disabled={busy} className="p-1 rounded bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-30" data-testid={`coach-editor-set-plus-${i}`}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      {/* Reps (read-only display) */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-blue-300/70">Reps</span>
                        <span className="text-white font-mono">{getReps(ex)}</span>
                      </div>
                      {/* Weight */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-blue-300/70">Kg</span>
                        <input
                          type="number" step="0.5" min="0"
                          value={weightDraft[i] ?? ''}
                          onChange={(e) => setWeightDraft((d) => ({ ...d, [i]: e.target.value }))}
                          onBlur={() => commitWeight(i)}
                          onKeyDown={(e) => { if (e.key === 'Enter') commitWeight(i); }}
                          disabled={busy}
                          className="w-20 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                          data-testid={`coach-editor-weight-${i}`}
                        />
                      </div>
                      {/* Superset */}
                      <div className="flex items-center gap-1.5 ml-auto">
                        <button onClick={() => toggleSuperset(i)} disabled={busy}
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded border ${ex.is_superset ? 'bg-orange-500/20 text-orange-200 border-orange-400/50' : 'bg-slate-700 text-blue-200 border-slate-600'} disabled:opacity-40`}
                          data-testid={`coach-editor-ss-toggle-${i}`}>
                          <Link2 className="w-3.5 h-3.5" /> Superset
                        </button>
                        {ex.is_superset && (
                          <select value={ex.superset_group || 'A'} onChange={(e) => setSupersetGroup(i, e.target.value)} disabled={busy}
                            className="bg-slate-900 border border-slate-600 rounded px-1.5 py-1 text-white text-xs" data-testid={`coach-editor-ss-group-${i}`}>
                            {SS_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            }) : (
              /* LOGGED VALUES — correct what the client actually entered */
              <>
                <p className="text-xs text-blue-300/60 pb-1">Correct the client's real logged kg / reps / RPE. Add or remove set rows as needed. The client will get a "corrected values" prompt on their screen.</p>
                {exercises.map((ex, i) => {
                  const cardio = isCardio(ex);
                  const rows = setDrafts[i] || [];
                  return (
                    <div key={`log-${ex.exercise_id}-${i}`} className="bg-slate-800/60 rounded-xl p-3 border border-slate-700/60" data-testid={`coach-logged-ex-${i}`}>
                      <div className="text-white font-semibold text-sm mb-2">
                        {ex.exercise_name_es || ex.exercise_name}
                        {cardio && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">Cardio</span>}
                      </div>
                      {rows.length === 0 && <p className="text-xs text-blue-300/50 mb-2">{cardio ? 'No rounds logged yet.' : 'No sets logged yet.'}</p>}
                      <div className="space-y-1.5">
                        {rows.map((s, si) => (
                          <div key={si} className="flex items-center gap-2" data-testid={`coach-logged-row-${i}-${si}`}>
                            <span className="text-xs text-blue-300/60 w-6">#{si + 1}</span>
                            {cardio ? (
                              <>
                                <label className="text-[10px] text-blue-300/60">Time (mm:ss)
                                  <input type="text" inputMode="numeric" placeholder="2:30" value={s.time}
                                    onChange={(e) => updateSetDraft(i, si, 'time', e.target.value)}
                                    className="ml-1 w-16 bg-slate-900 border border-slate-600 rounded px-1.5 py-1 text-white text-sm"
                                    data-testid={`coach-logged-time-${i}-${si}`} />
                                </label>
                                <label className="text-[10px] text-blue-300/60">Rest (s)
                                  <input type="number" min="0" value={s.rest_time}
                                    onChange={(e) => updateSetDraft(i, si, 'rest_time', e.target.value)}
                                    className="ml-1 w-16 bg-slate-900 border border-slate-600 rounded px-1.5 py-1 text-white text-sm"
                                    data-testid={`coach-logged-rest-${i}-${si}`} />
                                </label>
                              </>
                            ) : (
                              <>
                                <label className="text-[10px] text-blue-300/60">Reps
                                  <input type="number" min="0" value={s.reps}
                                    onChange={(e) => updateSetDraft(i, si, 'reps', e.target.value)}
                                    className="ml-1 w-14 bg-slate-900 border border-slate-600 rounded px-1.5 py-1 text-white text-sm"
                                    data-testid={`coach-logged-reps-${i}-${si}`} />
                                </label>
                                <label className="text-[10px] text-blue-300/60">Kg
                                  <input type="number" step="0.5" min="0" value={s.weight}
                                    onChange={(e) => updateSetDraft(i, si, 'weight', e.target.value)}
                                    className="ml-1 w-16 bg-slate-900 border border-slate-600 rounded px-1.5 py-1 text-white text-sm"
                                    data-testid={`coach-logged-weight-${i}-${si}`} />
                                </label>
                                <label className="text-[10px] text-blue-300/60">RPE
                                  <input type="number" step="0.5" min="0" max="10" value={s.rpe}
                                    onChange={(e) => updateSetDraft(i, si, 'rpe', e.target.value)}
                                    className="ml-1 w-14 bg-slate-900 border border-slate-600 rounded px-1.5 py-1 text-white text-sm"
                                    data-testid={`coach-logged-rpe-${i}-${si}`} />
                                </label>
                              </>
                            )}
                            <button onClick={() => removeSetDraft(i, si)} disabled={busy}
                              className="p-1 rounded bg-red-600/50 hover:bg-red-500 text-white disabled:opacity-30"
                              data-testid={`coach-logged-remove-row-${i}-${si}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-2.5">
                        <button onClick={() => addSetDraft(i)} disabled={busy}
                          className="flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-blue-100 disabled:opacity-40"
                          data-testid={`coach-logged-add-row-${i}`}><Plus className="w-3.5 h-3.5" /> {cardio ? 'Add round' : 'Add set'}</button>
                        <button onClick={() => saveActualSets(i)} disabled={busy}
                          className="ml-auto text-xs font-semibold px-3 py-1.5 rounded bg-orange-500 text-black hover:bg-orange-400 disabled:opacity-40"
                          data-testid={`coach-logged-save-${i}`}>Save values</button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Footer */}
          {tab === 'structure' && (
            <div className="p-4 border-t border-slate-700">
              <button onClick={() => setPicker({ mode: 'add' })} disabled={busy}
                className="w-full flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 text-white disabled:opacity-50"
                data-testid="coach-editor-add-btn">
                <Plus className="w-4 h-4" /> Add exercise
              </button>
            </div>
          )}
        </div>
      </div>

      {picker && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/80 p-3 sm:p-4"
          data-testid="coach-editor-picker" onClick={() => setPicker(null)}>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-white font-bold text-sm">
                {picker.mode === 'replace' ? 'Substitute exercise' : 'Add exercise'}
                {picker.mode === 'add' && <span className="text-blue-300/60 font-normal ml-2">— pick several, then close</span>}
              </h3>
            </div>
            <ExerciseSelectorGrid
              exercises={pickerExercises}
              onSelectExercise={handlePick}
              onClose={() => setPicker(null)}
              isTemplateMode={false}
              exerciseUsage={exerciseUsage}
              selectedExercises={picker.mode === 'add' ? exercises : null}
              onMoveSelected={picker.mode === 'add' ? moveExercise : undefined}
              onRemoveSelected={picker.mode === 'add' ? removeExercise : undefined}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default LiveBoardCoachEditor;
